import { CondicaoPagamentoModoEnum } from '../../../../../shared/enums/financas/condicao-pagamento-modo.enum';
import { UtilService } from '../../../../../shared/services/util.service';
import { centavos } from './venda-pagamento.calculo';
import { proximoDiaUtil } from './venda-pagamento.dia-util';

/**
 * As contas das parcelas de UM recebimento (§8.5 e §8.7), como funções puras: quem monta o form
 * chama e grava. Aqui não existe FormGroup, e é o que permite testar as contas de cabeça.
 *
 * TODO dinheiro é feito em CENTAVOS (inteiros), e o resto do arredondamento vai para a ÚLTIMA
 * parcela — é a regra escrita da story (§8.7, "o resto do arredondamento na última parcela").
 * O protótipo pôs o resto na primeira ao gerar e na última ao redistribuir; seguimos a story.
 */

/** A condição cadastrada, no que interessa para os dias — espelho do `select` do Finanças. */
export interface CondicaoParaParcelas {
    idCondicaoPagamentoModo: number;
    numeroParcelas: number;
    intervaloDias: number;
    diasPorParcela: { ordem: number; dias: number }[];
    taxaJuros: number;
}

/** Uma parcela calculada — o que vira linha da grade. */
export interface ParcelaCalculada {
    numeroParcela: number;
    dias: number;
    dataVencimento: Date;
    valor: number;
}

/**
 * Condição LIVRE (§8.5): `N` é uma parcela em N dias; `Nx` são N parcelas a cada 30 dias a partir
 * do último prazo informado. Separadores: espaço, vírgula, ponto-e-vírgula ou `+`.
 * Exemplos: "30 60" → [30, 60]; "3x" → [30, 60, 90]; "15 +2x" → [15, 45, 75].
 * Devolve vazio quando nenhum número válido foi encontrado — quem chama bloqueia com a mensagem.
 */
export function interpretarCondicaoLivre(texto: string | null | undefined): number[] {
    const dias: number[] = [];

    for (const token of String(texto ?? '').trim().toLowerCase().split(/[\s,;+]+/).filter(Boolean)) {
        const vezes = token.match(/^(\d+)x$/);

        if (vezes) {
            const base = dias.length ? dias[dias.length - 1] : 0;

            for (let i = 1; i <= Number(vezes[1]); i++) dias.push(base + i * 30);

            continue;
        }

        if (/^\d+$/.test(token)) dias.push(Number(token));
    }

    return dias;
}

/** Os dias de vencimento de uma condição CADASTRADA, conforme o modo dela. */
export function diasDaCondicao(condicao: CondicaoParaParcelas): number[] {
    if (condicao.idCondicaoPagamentoModo === CondicaoPagamentoModoEnum.DiasPorParcela) {
        return [...(condicao.diasPorParcela ?? [])].sort((a, b) => a.ordem - b.ordem).map((p) => p.dias);
    }

    const dias: number[] = [];

    for (let i = 1; i <= Math.max(1, condicao.numeroParcelas || 1); i++) {
        dias.push(i * (condicao.intervaloDias || 30));
    }

    return dias;
}

/**
 * Parcelamento do INTERMEDIADOR (T07): a parcela i vence em i × prazo da condição comercial, contado da emissão,
 * pulando para o próximo dia útil quando o intermediador pede. Mesma conta de `IntermediadorCalculoHelper`.
 */
export function diasDoParcelamentoDoIntermediador(parcelas: number, prazoDias: number, emissao: Date, usarProximoDiaUtil: boolean): number[] {
    const dias: number[] = [];

    for (let i = 1; i <= Math.max(1, parcelas); i++) {
        const vencimento = somarDias(emissao, i * prazoDias);

        dias.push(diasEntre(emissao, usarProximoDiaUtil ? proximoDiaUtil(vencimento) : vencimento));
    }

    return dias;
}

/**
 * Gera as parcelas de um recebimento: o VALOR do recebimento dividido em centavos, resto na
 * última, vencimentos contados a partir da data de emissão do documento.
 *
 * A taxa da condição NÃO entra (decisão D-B, 26/09, revendo a D9): a folha exige Σ parcelas =
 * valor do recebimento e é o Finanças, ao gerar os títulos, quem aplica juros — aqui a taxa é
 * só a simulação da §8.6.
 */
export function gerarParcelas(valorRecebimento: number, dias: number[], emissao: Date): ParcelaCalculada[] {
    // A soma das parcelas é SEMPRE idêntica ao valor: o resto dos centavos vai para a última (a folha confere ao centavo).
    if (!dias.length) return [];

    const total = centavos(Math.max(0, valorRecebimento));
    const base = Math.floor(total / dias.length);
    const resto = total - base * dias.length;

    return dias.map((d, i) => ({
        numeroParcela: i + 1,
        dias: d,
        dataVencimento: somarDias(emissao, d),
        valor: (base + (i === dias.length - 1 ? resto : 0)) / 100,
    }));
}

/**
 * Redistribuição automática (§8.7): editar o valor de uma parcela regera as DEMAIS, dividindo o
 * saldo entre elas.
 *   - intermediária → o saldo vai para as SEGUINTES;
 *   - última → o saldo vai para as ANTERIORES que ainda não foram fixadas pelo usuário.
 * Fixadas (`isValorFixadoUsuario`) ficam como estão; a editada passa a fixada. Centavos, resto na
 * última parcela redistribuída. Não há mensagem de soma: a soma fecha por construção.
 */
export function redistribuirParcelas<T extends { valor: number; isValorFixadoUsuario: boolean }>(
    parcelas: T[],
    indiceEditado: number,
    totalRecebimento: number,
): T[] {
    const totalC = centavos(totalRecebimento);
    const resultado = parcelas.map((p, i) => (i === indiceEditado ? { ...p, isValorFixadoUsuario: true } : { ...p }));

    const ehUltima = indiceEditado >= parcelas.length - 1;
    const alvos = ehUltima
        ? resultado.map((p, i) => i).filter((i) => i !== indiceEditado && !resultado[i].isValorFixadoUsuario)
        : resultado.map((p, i) => i).filter((i) => i > indiceEditado);

    if (!alvos.length) return resultado;

    const fixo = resultado.reduce((soma, p, i) => (alvos.includes(i) ? soma : soma + centavos(p.valor)), 0);
    const saldo = Math.max(0, totalC - fixo);
    const base = Math.floor(saldo / alvos.length);
    const resto = saldo - base * alvos.length;

    alvos.forEach((indice, posicao) => {
        resultado[indice] = {
            ...resultado[indice],
            valor: (base + (posicao === alvos.length - 1 ? resto : 0)) / 100,
        };
    });

    return resultado;
}

/** Alterar o vencimento no modal recalcula os Dias a partir da emissão (§8.7.1). */
export function diasEntre(emissao: Date, vencimento: Date): number {
    const umDia = 24 * 60 * 60 * 1000;
    const a = new Date(emissao.getFullYear(), emissao.getMonth(), emissao.getDate()).getTime();
    const b = new Date(vencimento.getFullYear(), vencimento.getMonth(), vencimento.getDate()).getTime();

    return Math.max(0, Math.round((b - a) / umDia));
}

export function somarDias(data: Date, dias: number): Date {
    const nova = new Date(data.getFullYear(), data.getMonth(), data.getDate());

    nova.setDate(nova.getDate() + dias);

    return nova;
}

/** Soma dos valores em dinheiro, ABNT em 2 casas — para a barra de cobertura e a validação. */
export function somarValores(valores: (number | null | undefined)[]): number {
    // `reduce<number>`: sem o tipo explícito o acumulador herda `number | null | undefined` do
    // array, e o TypeScript recusa `soma + ...`. O `Number(v) || 0` já saneia cada elemento.
    return UtilService.arredondarAbnt(valores.reduce<number>((soma, v) => soma + (Number(v) || 0), 0), 2);
}
